/**
 * Infiltration Run — pure calculation system.
 * No DOM, no game-state coupling: every function takes plain values so it can
 * run under Node and be unit-tested deterministically (rolls are passed in).
 */

import type { InfilChoice, InfilEvent, CareerTier } from '../data/infiltration.js';
import { CAREER_TIERS } from '../data/infiltration.js';

/** Success-rate slope: each stat point over difficulty adds K percentage points. */
export const SUCCESS_K = 2;
/** Minimum success rate for a judged choice (never below this except by design). */
export const FLOOR_PCT = 5;
/** Detection is caught the instant it reaches this. */
export const DETECTION_CATCH = 100;
/** Cooldown after a failed (caught) run, in ms. */
export const FAIL_COOLDOWN_MS = 60_000;
/** Flat IP granted per successful choice at final resolve. */
export const IP_PER_CHOICE_FLAT = 120;
/** Seconds of current production added per successful choice (keeps rewards relevant late-game). */
export const IP_CPS_SECONDS = 8;
/** Stat growth applied to every base stat after a successful run. */
export const STAT_GROWTH_ON_SUCCESS = 1;

/** clamp helper */
function clamp(n: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, n));
}

/**
 * Success rate (0..100) of a choice for an agent with `agentStat` in its stat.
 * A 'none' choice is always 100 (safe, no judgement).
 * Formula: clamp(baseSuccess + (agentStat - difficulty) * K, FLOOR_PCT, 100)
 */
export function computeSuccessRate(choice: InfilChoice, agentStat: number): number {
  if (choice.statType === 'none') return 100;
  const raw = choice.baseSuccess + (agentStat - choice.difficulty) * SUCCESS_K;
  return clamp(Math.round(raw), FLOOR_PCT, 100);
}

/** Whether a roll (0..100) beats the given success rate. Deterministic. */
export function rollSuccess(successRate: number, roll: number): boolean {
  return roll < successRate;
}

/** Detection added by resolving a choice (success adds less, failure adds more). */
export function detectionDelta(choice: InfilChoice, success: boolean): number {
  return success ? choice.detectionOnSuccess : choice.detectionOnFail;
}

/** Clamp a detection value into the 0..100 range. */
export function clampDetection(detection: number): number {
  return clamp(detection, 0, DETECTION_CATCH);
}

/** Whether accumulated detection means the agent is caught. */
export function isCaught(detection: number): boolean {
  return detection >= DETECTION_CATCH;
}

/** Final success chance (0..100): the lower the detection, the safer the extraction. */
export function finalSuccessChance(detection: number): number {
  return clamp(100 - detection, 0, 100);
}

/** Resolve the final run outcome from detection and a roll (0..100). Deterministic. */
export function resolveFinalOutcome(detection: number, roll: number): 'success' | 'fail' {
  return roll < finalSuccessChance(detection) ? 'success' : 'fail';
}

/** Ambition weight of a choice — how much career it can yield if the run survives. */
export function choiceAmbition(choice: InfilChoice): number {
  switch (choice.tag) {
    case 'safe': return 0;
    case 'risky': return 1;
    case 'aggressive': return 2;
  }
}

/**
 * Determine the career tier earned. Requires surviving (survived === true).
 * Higher ambition (risk successfully taken) yields a higher tier — but you must
 * keep detection low enough to actually extract, which is the core trade-off.
 */
export function pickCareer(ambition: number, survived: boolean): CareerTier | null {
  if (!survived) return null;
  if (ambition >= 10) return 'warrior';
  if (ambition >= 8) return 'director';
  if (ambition >= 6) return 'executive';
  if (ambition >= 4) return 'manager';
  if (ambition >= 2) return 'clerk';
  if (ambition >= 1) return 'guard';
  return 'cleaner';
}

/** Assert a value is a known career tier (defensive; keeps CAREER_TIERS referenced). */
export function isCareerTier(value: string): value is CareerTier {
  return (CAREER_TIERS as string[]).includes(value);
}

/** IP reward at final resolve: flat per choice + a slice of current production. */
export function computeRunReward(accumulatedIp: number, successfulChoices: number, cps: number): number {
  const perChoice = IP_PER_CHOICE_FLAT + Math.max(0, cps) * IP_CPS_SECONDS;
  return Math.floor(accumulatedIp + successfulChoices * perChoice);
}

/** Infiltration difficulty stage, derived from how many runs the player has cleared. */
export function getInfiltrationStage(totalSuccess: number): number {
  return Math.min(Math.max(0, totalSuccess), 2);
}

/** Number of events in a normal run (5 or 6), chosen via randomFn (0..1). */
export function pickEventCount(randomFn: () => number = Math.random): number {
  return randomFn() < 0.5 ? 5 : 6;
}

/**
 * Draw a run's event queue: shuffle the stage-eligible pool and take `count`
 * ids with no repeats. randomFn is injectable for deterministic tests.
 */
export function pickEventQueue(
  pool: InfilEvent[],
  count: number,
  stage: number,
  randomFn: () => number = Math.random,
): string[] {
  let eligible = pool.filter((e) => (e.minStage ?? 0) <= stage);
  if (eligible.length < count) eligible = pool.slice();
  const arr = eligible.slice();
  // Fisher-Yates shuffle
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(randomFn() * (i + 1));
    const tmp = arr[i];
    arr[i] = arr[j];
    arr[j] = tmp;
  }
  return arr.slice(0, count).map((e) => e.id);
}

/**
 * Tutorial forced outcome: in the scripted first run, safe/risky choices always
 * succeed and aggressive choices always fail — teaching that gambling for the top
 * tier backfires. Returns the forced success value for a choice.
 */
export function tutorialForcedSuccess(choice: InfilChoice): boolean {
  return choice.tag !== 'aggressive';
}
